// src/pages/PublicHousingUAPage.js
// UNPUBLISHED (unlinked) per-development PUBLIC HOUSING utility allowance
// viewer. Public housing allowances follow 24 CFR 965 Subpart E (or the
// state parallel) — set per development, tenant-paid utilities only.
// Data comes live from the compliance service's phEngine (HUSM
// engineering-estimate consumption + the org's cited tariffs), draft
// until the development's metering/equipment/consumption data replace
// the flagged assumptions.
//
// Route: /utility-allowances/public-housing/:property — not linked from
// the hub, nav, or anywhere else.

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Brand from '../components/Brand';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const T = {
  blue600: '#2563eb',
  blue100: '#dbeafe',
  gray900: '#111827',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

export default function PublicHousingUAPage() {
  const mobile = useIsMobile();
  const { property } = useParams();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${COMPLIANCE_API}/api/utility-allowance/public-housing/${encodeURIComponent(property || '')}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setSchedule(body?.schedule || null); })
      .catch(() => { if (!cancelled) setSchedule(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [property]);

  const dev = schedule;

  return (
    <div style={{ fontFamily: T.font, minHeight: '100vh', background: T.gradient, display: 'flex', flexDirection: 'column' }}>
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: '1400px', margin: '0 auto', flexWrap: 'wrap' }}>
          <Brand as="a" href="https://gethousingai.com" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          {dev && (
            <span style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '0.95rem' : '1.1rem' }}>
              {dev.orgName} &middot; {dev.name}
            </span>
          )}
          <span style={{
            fontSize: mobile ? '0.72rem' : '0.85rem',
            padding: mobile ? '0.2rem 0.55rem' : '0.25rem 0.75rem',
            background: T.blue100,
            color: T.blue600,
            borderRadius: T.radiusPill,
            fontWeight: 600,
          }}>
            Public Housing Utility Allowance
          </span>
        </div>
      </header>

      <main style={{ padding: mobile ? '1.5rem 0.75rem' : '3rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: T.gray500 }}>Loading&hellip;</div>
          ) : !dev ? (
            <div style={{ background: T.gray100, border: `1px solid ${T.gray200}`, borderRadius: T.radius, padding: '3rem', textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: T.gray900, margin: '0 0 0.5rem' }}>Unknown development</h3>
              <p style={{ color: T.gray500, fontSize: '0.9rem', margin: 0 }}>
                No public housing utility allowance for &ldquo;{property}&rdquo;.
              </p>
            </div>
          ) : (
            <>
              {dev.draft && (
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: T.radiusSm,
                  padding: '0.85rem 1.1rem',
                  marginBottom: '1.25rem',
                  fontSize: '0.88rem',
                  color: '#92400e',
                }}>
                  Draft — engineering estimates. Rows marked &ldquo;assumed&rdquo; reflect assumed
                  metering and equipment pending the development&rsquo;s own data; not an adopted schedule.
                </div>
              )}

              <div style={{ background: T.white, border: `1px solid ${T.gray200}`, borderRadius: T.radius, boxShadow: T.shadowSm, padding: mobile ? '1.25rem 1rem' : '1.75rem' }}>
                <h1 style={{ fontSize: mobile ? '1.15rem' : '1.4rem', fontWeight: 800, color: T.gray900, margin: '0 0 0.25rem', letterSpacing: '-0.02em' }}>
                  Utility Allowances &mdash; {dev.name}
                </h1>
                <p style={{ color: T.gray600, fontSize: '0.88rem', margin: '0 0 1rem' }}>
                  {dev.orgName} &middot; {dev.address} &middot; {dev.program} &middot; {dev.regulation}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
                  <span style={{ padding: '0.15rem 0.6rem', background: T.gray100, color: T.gray600, fontSize: '0.75rem', borderRadius: T.radiusPill, fontFamily: 'monospace' }}>{dev.phaCode}</span>
                  <span style={{ padding: '0.15rem 0.6rem', background: T.gray100, color: T.gray600, fontSize: '0.75rem', borderRadius: T.radiusPill }}>{dev.units} units</span>
                  <span style={{ padding: '0.15rem 0.6rem', background: T.gray100, color: T.gray600, fontSize: '0.75rem', borderRadius: T.radiusPill }}>
                    Development No. {dev.developmentNo || '—'}
                  </span>
                  <span style={{ padding: '0.15rem 0.6rem', background: T.gray100, color: T.gray600, fontSize: '0.75rem', borderRadius: T.radiusPill }}>
                    Effective {dev.effectiveDate || '—'}
                  </span>
                </div>

                {/* Tenant-paid allowances */}
                <div style={{ overflowX: 'auto', border: `1px solid ${T.gray200}`, borderRadius: T.radiusSm }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: T.gray100 }}>
                        <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', color: T.gray600, fontWeight: 600 }}>Tenant-Paid Utility</th>
                        {dev.bedrooms.map((b) => (
                          <th key={b} style={{ textAlign: 'center', padding: '0.6rem 0.85rem', color: T.gray600, fontWeight: 600 }}>{b}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dev.rows.map((row) => (
                        <tr key={row.label} style={{ borderTop: `1px solid ${T.gray100}` }}>
                          <td style={{ padding: '0.55rem 0.85rem', color: T.gray900 }}>
                            {row.label}
                            {row.assumed && <span style={{ color: T.gray400, fontSize: '0.75rem' }}> (assumed)</span>}
                          </td>
                          {row.values.map((v, i) => (
                            <td key={i} style={{ textAlign: 'center', padding: '0.55rem 0.85rem', color: T.gray900, fontVariantNumeric: 'tabular-nums' }}>
                              ${v}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Authority-paid services: no allowance */}
                {dev.phaPaid?.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>
                      No allowance &mdash; paid by the authority:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', color: T.gray500, fontSize: '0.8rem', lineHeight: 1.6 }}>
                      {dev.phaPaid.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </div>
                )}

                {/* Rates used */}
                {dev.rates && Object.keys(dev.rates).length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>Rates used:</div>
                    {Object.entries(dev.rates).map(([k, r]) => (
                      <div key={k} style={{ fontSize: '0.78rem', color: T.gray500, lineHeight: 1.55 }}>
                        ${r.value}/kWh{r.fixed ? ` + $${Number(r.fixed).toFixed(2)}/mo customer charge` : ''} &middot; {r.source}
                        {r.url && <> &middot; <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: T.blue600 }}>source</a></>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Assumptions on record */}
                {dev.assumptions?.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: T.gray600, marginBottom: '0.35rem' }}>Pending verification:</div>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem', color: T.gray500, fontSize: '0.78rem', lineHeight: 1.6 }}>
                      {dev.assumptions.map((a) => <li key={a}>{a}</li>)}
                    </ul>
                  </div>
                )}

                <p style={{ fontSize: '0.78rem', color: T.gray500, lineHeight: 1.55, margin: '1rem 0 0' }}>
                  {dev.methodNote}
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#d1d5db' }}>© 2026 HousingAI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
